/* eslint-disable react/no-string-refs */
import React from 'react';
import PropTypes from 'prop-types';
import Radium from 'radium';
import { Editor } from '@ublend-npm/th90-editor';
import PLUGINS from '@ublend-npm/th90-plugins';

import style from './style';
import withErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { ERROR_EDITOR } from '../../../../constants/texts';

const EditorPlugins = PLUGINS.map((sp) => sp.plugin);
const noop = () => null;

const pluginConfigToProps = (config) =>
  Object.assign(
    {},
    ...Object.entries(config).map(([key, props]) => ({
      [`plugin-config-${key}`]: props,
    }))
  );

@Radium
class EditorPlus extends React.Component {
  constructor(props) {
    super(props);

    this.handleEditorChanged = this.handleEditorChanged.bind(this);
    this.handleHeightChange = this.handleHeightChange.bind(this);
    this.focus = this.focus.bind(this);
    this.clear = this.clear.bind(this);
    this.isEmpty = this.isEmpty.bind(this);

    this._trackedStart = false; // Avoid useless rerenders
    this._currentHeight = 0;

    this.editor = undefined;
  }

  clear() {
    this.editor.clear();
  }

  focus() {
    if (this.editor) {
      this.editor.focus();
    }
  }

  blur() {
    this.editor.blur();
  }

  /* eslint-disable class-methods-use-this */
  isEmpty(contentState) {
    return !contentState.hasText();
  }
  /* eslint-enable class-methods-use-this */

  handleHeightChange() {
    if (
      this.refs.container &&
      this._currentHeight !== this.refs.container.offsetHeight
    ) {
      this._currentHeight = this.refs.container.offsetHeight;
      this.props.onHeightChange(this._currentHeight);
    }
  }

  handleEditorChanged(state, ready) {
    this.props.onChange(state, ready);

    this.forceUpdate();

    if (this.props.readOnly) return;

    // wait for animation time
    setTimeout(() => {
      this.handleHeightChange();
    }, 500);

    if (this._trackedStart && this.isEmpty(state.getCurrentContent())) {
      this._trackedStart = false;
    }

    if (!this._trackedStart && !this.isEmpty(state.getCurrentContent())) {
      this._trackedStart = true;
      this.props.trackEvent();
    }
  }

  import(rawState) {
    return this.editor.setEditorState(rawState);
  }

  export() {
    return this.editor.export();
  }

  render() {
    const {
      actionButton,
      secondaryAction,
      animatedPlaceholder,
      autoFocus,
      disabled,
      handleReturn,
      initialEditorState,
      maxEditorHeight,
      paddings,
      placeholder,
      onHeightChange,
      pluginsConfigs,
      rawEditorState,
      readOnly,
      whiteBackground,
    } = this.props;

    const confs = pluginConfigToProps(pluginsConfigs);

    return (
      <div ref="container" style={style.container}>
        <div
          style={{
            ...style.editorAddonsWrapper,
            paddingTop: whiteBackground ? 25 : 0,
            height: '100%',
          }}
        >
          {whiteBackground ? <div style={style.whiteBackground} /> : null}
          <Editor
            th90Ref={(r) => {
              if (!this.editor) {
                this.editor = r;
                if (autoFocus) {
                  this.focus();
                }
              }
            }}
            onChange={this.handleEditorChanged}
            disabled={disabled}
            placeholder={placeholder}
            animatedPlaceholder={animatedPlaceholder}
            handleReturn={handleReturn}
            maxEditorHeight={maxEditorHeight}
            onHeightChange={onHeightChange}
            rawEditorState={rawEditorState}
            initialEditorState={initialEditorState}
            disabledOverlayPaddings={paddings}
            readOnly={readOnly}
            {...confs}
            actionButton={actionButton}
            secondaryAction={secondaryAction}
            plugins={EditorPlugins}
          />
        </div>
      </div>
    );
  }
}

EditorPlus.propTypes = {
  animatedPlaceholder: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  handleReturn: PropTypes.func,
  onHeightChange: PropTypes.func,
  whiteBackground: PropTypes.bool,
  maxEditorHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /* eslint-disable react/forbid-prop-types */
  actionButton: PropTypes.object,
  secondaryAction: PropTypes.object,
  rawEditorState: PropTypes.object,
  initialEditorState: PropTypes.object,
  pluginsConfigs: PropTypes.object,
  paddings: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */
  readOnly: PropTypes.bool,
  trackEvent: PropTypes.func.isRequired,
};

EditorPlus.defaultProps = {
  actionButton: undefined,
  secondaryAction: undefined,
  animatedPlaceholder: false,
  autoFocus: false,
  disabled: false,
  handleReturn: null,
  initialEditorState: undefined,
  maxEditorHeight: undefined,
  onChange: noop,
  onHeightChange: noop,
  paddings: undefined,
  placeholder: '',
  pluginsConfigs: {},
  rawEditorState: undefined,
  readOnly: false,
  whiteBackground: false,
};

export default withErrorBoundary(EditorPlus, ERROR_EDITOR);
